import {
	faEquals,
	faPlus,
	faSearch,
	faTimes,
} from '@fortawesome/free-solid-svg-icons';
import {
	ChangeDetectorRef,
	Component,
	Input,
	OnDestroy,
	ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import _ from 'lodash';
import {
	assertExhaustive,
	BaseType,
	ProductType,
	RecipeCalculatorType,
	RecipeMode,
	RecipePageType,
	ScanLogWorkflow,
	Units,
} from 'src/app/app.enums';
import { defaultSelectOptions } from 'src/app/components/default-options';
import { capitalize } from 'src/app/app.util';

import {
	Recipe,
	RecipeBase,
	RecipeData,
	RecipePermissions,
	ScannedRecipeData,
} from 'src/app/models/recipe.model';
import {
	IOrderFulfillmentContent,
	PatientEhrOrderModel,
	PatientModel,
	PatientOrderResponseModel,
} from 'src/app/models/patient.model';

import { BaseService } from 'src/app/services/base.service';
import { InventoryService } from 'src/app/services/inventory.service';

import {
	calculateDesiredVolume,
	calculateFeedVolume,
	calculateVolumeOnHand,
	displayAdditives,
	getAdditivesByCalorieDensity,
	getRecipeBasesFromMilkBottles,
	getCalorieRangeFromRecipe,
	getFinalVolume,
	getMinimumPossibleVolume,
	getScanDataAdditives,
	getTotalAdditiveAmount,
	getCombinedBaseName,
} from 'src/app/utils/recipe-calculator.util';
import { resolveBase } from 'src/app/utils/base-product.util';
import { SelectedProductIngredient } from 'src/app/models/prep-feed.model';
import { StorageService } from 'src/app/services/storage.service';
import { Product } from 'src/app/models/product.model';
import { environment } from 'src/environments/environment';
import { Additive, IAdditive } from '../../models/additive.model';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { isPrepared2 } from '../../utils/inventory.util';
import {
	IContentProduct,
	OrderSelectComponent,
} from '../../components/order-select/order-select.component';
import {
	calculateNumberOfFeeds,
	parseIntervalToHours,
} from '../../utils/interval.util';
import { getPrepTimeframeHours } from '../../utils/interval.util';
import { MilkBottleModel } from 'src/app/models/milk.model';
import { UniversalRecipeCalculatorService } from 'src/app/services/universal-recipe-calculator.service';
import {
	RecipeNotSatisfiableApiError,
	UrcNotSatisfiableError,
	UrcResult,
	UrcUserInput,
} from 'src/app/models/universal-recipe-calculator.model';
import type { CalorieUom } from 'src/app/utils/calorie-density.util';
import {
	CALORIE_DENSITY_STEP,
	formatCaloricDensity,
	fromOz,
	getDensityRange,
	stepCeil,
	stepFloor,
	stepRound,
	toWorkingUom,
} from 'src/app/utils/calorie-density.util';
import { blockNonWholeNumberKeys } from 'src/app/utils/text-field.util';

/** Calculator boxes that only take whole numbers. */
const WholeNumberControl = {
	NumberOfFeeds: 'numberOfFeeds',
	Volume: 'volume',
	VolumeVOH: 'volumeVOH',
} as const;
type WholeNumberControl = (typeof WholeNumberControl)[keyof typeof WholeNumberControl];

@Component({
	selector: 'app-new-recipe-calculator',
	templateUrl: './new-recipe-calculator.modal.html',
	styleUrls: ['./new-recipe-calculator.modal.scss'],
})
export class NewRecipeCalculatorModal implements OnDestroy {
	@ViewChild('orderSelectRef') orderSelectRef: OrderSelectComponent;

	@Input() pageType: RecipePageType;
	@Input() recipeType: RecipeCalculatorType;
	@Input() recipePermissions: RecipePermissions;
	@Input() patients: PatientModel[] = [];
	@Input() baseProductId: Product['id'];
	@Input() selectedOrder: PatientEhrOrderModel;
	@Input() orders: PatientEhrOrderModel[];
	@Input() orderObject: PatientOrderResponseModel;
	@Input() recipeData: RecipeData; // saved recipe data
	@Input() volume = 0;
	@Input() scannedMilkBottles: MilkBottleModel[] = [];
	@Input() workflow: ScanLogWorkflow;
	@Input() selectedProductIngredients: SelectedProductIngredient[] = [];
	@Input() equivalentBaseProductIds: string[] = [];

	// FA icons
	faPlus = faPlus;
	faTimes = faTimes;
	faEquals = faEquals;
	faSearch = faSearch;
	faChevronRight = faChevronRight;

	defaultSelectOptions = defaultSelectOptions;
	hasChanges = false;
	RecipeMode = RecipeMode;
	blockNonWholeNumberKeys = blockNonWholeNumberKeys;
	mode: RecipeMode = RecipeMode.DV;
	recipes: Recipe[];
	form: FormGroup;
	baseOptions: RecipeBase[];
	scannedMilkBottleBases: RecipeBase[] = [];
	/** Recipe-derived, so left unset in Manual Prep mode — see handleModeSelected. */
	calorieDensityOptions: number[] | undefined;
	additiveTypeOptions: IAdditive[];

	scanData: ScannedRecipeData;
	scannedAdditives: IAdditive[] = [];
	scannedProducts: Product[] = [];

	additiveCandidates: AdditiveCandidate[] = [];

	fulfillmentBaseOptions: IOrderFulfillmentContent[] = [];
	fulfillmentAdditiveOptions: IOrderFulfillmentContent[] = [];
	nonCaloricAdditiveItems: IOrderFulfillmentContent[] = [];
	urcExemptAdditiveItems: IOrderFulfillmentContent[] = [];

	finalVolume: number | null = null;
	baseNeeded: number | null = null;

	volumeManuallyEdited = false;

	capitalize = capitalize;
	environment = environment;
	isPrepared2 = isPrepared2;
	StorageService = StorageService;

	private scannedProductIds = new Set<string>();
	private fullfillmentsProductIds = new Set<string>();

	// Representative id/label for a collapsed same-density human-milk base, reused on the recipe page.
	private combinedBaseProductId: string | null = null;
	private combinedBaseLabel: string | null = null;

	constructor(
		public base: BaseService,
		private fb: FormBuilder,
		private modalCtrl: ModalController,
		private inventoryService: InventoryService,
		private urcService: UniversalRecipeCalculatorService,
		private cdr: ChangeDetectorRef,
	) {}

	/**
	 * All RecipeBases from the selectedRecipes. This is unique on RecipeBase.baseProductId.
	 * Currently, there should only be one RecipeBase when calculating and validating.
	 */
	get selectedRecipesBases(): RecipeBase[] {
		if (this.mode === RecipeMode.MP) {
			console.log('BZBZ this is a test to unbreak PR');
			return [this.form.value.recipeBase];
		}

		if (!this.scanData?.selectedRecipes) {
			return null;
		}
		return _.chain(this.scanData.selectedRecipes)
			.flatMap((recipe) => recipe.recipe)
			.uniqBy((recipe) => recipe.baseProductId)
			.map((recipe) => ({
				baseProductId: recipe.baseProductId,
				baseName: recipe.baseName,
				baseType: recipe.baseType,
			}))
			.value();
	}

	async ionViewDidEnter() {
		if (!(await this.ensureConversionRateConfigured())) {
			return;
		}

		this.scanData?.scannedProducts
			?.map((p) => String(p.product.id))
			.forEach((id) => this.scannedProductIds.add(id));
		this.scannedAdditives.forEach((a) =>
			this.scannedProductIds.add(String(a.productId)),
		);
		this.scannedProducts.forEach((p) =>
			this.scannedProductIds.add(String(p.id)),
		);
		this.scannedMilkBottles.forEach((mb) =>
			mb.contents.forEach((c) =>
				this.scannedProductIds.add(String(c.contentProductId)),
			),
		);

		this.scannedMilkBottleBases = getRecipeBasesFromMilkBottles(
			this.scannedMilkBottles,
		);

		this.buildForm();
		// Force Angular to render the template so @ViewChild orderSelectRef is resolved
		this.cdr.detectChanges();

		const configs = StorageService.configs;
		if (!configs) return;

		await this.initPermissions();

		// initPermissions() may dismiss, but also guard here
		if (
			!this.recipePermissions?.volumeOnHand &&
			!this.recipePermissions?.desiredVolume &&
			!this.recipePermissions?.manualPrep
		) {
			return;
		}

		const initialMode =
			this.recipeData?.mode ??
			(this.recipePermissions?.desiredVolume
				? RecipeMode.DV
				: this.recipePermissions?.volumeOnHand
					? RecipeMode.VOH
					: RecipeMode.MP);

		// Set immediately so the segment highlights before any async work
		this.mode = initialMode;

		await this.initOrders();
		this.triggerOrderValidation();

		const effectiveMode = this.isRestrictedOnlyMode
			? RecipeMode.MP
			: initialMode;

		await this.handleModeSelected(effectiveMode);
		// Re-apply order autofill now that calorieDensityOptions is populated
		// (it was called earlier in initOrders() before options were available)
		this.autoFillFromOrder(this.selectedOrder);
	}

	/**
	 * Segment toggle handler (Desired Volume / Volume On Hand / Manual Prep)
	 */
	async handleModeToggleChange(ev: CustomEvent) {
		const nextMode = (ev?.detail as any)?.value as RecipeMode;
		if (!nextMode || nextMode === this.mode) {
			return;
		}
		await this.handleModeSelected(nextMode);
	}

	ngOnDestroy(): void {
		void this.base.clean(); // fire-and-forget
		// or: this.base.clean().catch(console.error);
	}

	buildForm() {
		this.form = this.fb.group({
			recipeBase: this.hasSelectableBase()
				? [null, Validators.required]
				: [null],

			// Calculator inputs
			volumePerFeed: [null],
			numberOfFeeds: [null],
			overfillPerFeed: [null],

			volume: [this.volume, Validators.required],
			volumeVOH: [null],
			calorieDensity: [null, Validators.required],
			additiveCandidate: [null],
			recipes: this.fb.array([
				this.fb.group({
					additive: [null, Validators.required],
				}),
			]),
			products: this.fb.array([
				this.fb.group({
					product: [null, Validators.required],
				}),
			]),
			fulfillmentBase: [null],
			fulfillmentAdditives: this.fb.array([]),
			manualPrepCalorieDensity: [null],
		});
	}

	/**
	 * DV mode convenience: compute "Desired Final Volume" from
	 * (volumePerFeed + overfillPerFeed) * numberOfFeeds.
	 */
	handleFeedInputsChanged() {
		if (!this.form) return;

		this.roundToWholeNumber(WholeNumberControl.NumberOfFeeds);
		const numberOfFeeds = this.form.value.numberOfFeeds;

		if (numberOfFeeds == null || numberOfFeeds === '') {
			return;
		}

		this.volumeManuallyEdited = false;

		const vpf = Number(this.form.value.volumePerFeed);
		const feeds = Number(numberOfFeeds);
		const over = Number(this.form.value.overfillPerFeed);

		const computed = calculateFeedVolume(vpf, over, feeds);
		if (computed === null) return;

		this.form.patchValue({ volume: Math.round(computed) }, { emitEvent: false });
		this.calculate();
	}

	handleVolumeDirectEdit() {
		this.roundToWholeNumber(
			this.mode === RecipeMode.VOH ? WholeNumberControl.VolumeVOH : WholeNumberControl.Volume,
		);
		if (this.mode !== RecipeMode.VOH && !this.volumeManuallyEdited) {
			this.volumeManuallyEdited = true;
			this.form.patchValue(
				{ volumePerFeed: null, overfillPerFeed: null, numberOfFeeds: null },
				{ emitEvent: false },
			);
		}
		this.calculate();
	}

	/**
	 * If a whole-number box still ends up with a decimal (for example, from a paste),
	 * round it to the closest whole number.
	 */
	private roundToWholeNumber(controlName: WholeNumberControl): void {
		const raw = this.form.value[controlName];
		if (raw == null || raw === '') return;

		const value = Number(raw);
		if (Number.isFinite(value) && !Number.isInteger(value)) {
			this.form.patchValue({ [controlName]: Math.round(value) }, { emitEvent: false });
		}
	}

	async initPermissions() {
		const showVOH = this.recipePermissions?.volumeOnHand;
		const showDV = this.recipePermissions?.desiredVolume;
		const showMP = this.recipePermissions?.manualPrep;
		if (showVOH && !showDV && !showMP) {
			this.mode = RecipeMode.VOH;
		} else if (!showVOH && showDV && !showMP) {
			this.mode = RecipeMode.DV;
		} else if (!showVOH && !showDV && showMP) {
			this.mode = RecipeMode.MP;
		} else if (!showVOH && !showDV && !showMP) {
			await this.modalCtrl.dismiss();
			alert(
				"ERROR: You don't have permission to access the recipe calculator. Please contact your administrator.",
			);
		}
	}

	/**
	 * Some users may only have permission for one recipe mode (VOH, DV, MP).
	 * In that case, we bypass the recipe mode selection and load the page.
	 */
	async initModeSelection(mode: RecipeMode) {
		if (mode) {
			await this.handleModeSelected(this.mode);
		}
	}

	async initOrders() {
		// assign destructured first item in the array
		const [patient] = this.patients;

		if (patient) {
			try {
				// get orders if not already loaded
				// Milk Room Prep already preloads orders
				if (!this.orders) {
					if (environment.settings.removeAllOrders) {
						console.warn('[Dev Settings] Removing all orders');
						this.orders = [];
					} else {
						await this.base.presentLoading('Loading Orders...');
						this.orderObject =
							await this.base.patientService.getPatientEhrOrders(
								patient.id,
								this.workflow,
							);
						this.orders = this.orderObject.orders;
					}
				}

				// default to the first order unless there is a selected order
				if (!this.selectedOrder) {
					this.selectedOrder = this.orders[0];
				} else {
					this.selectedOrder =
						this.orders.find((order) => order.id === this.selectedOrder.id) ||
						this.orders[0];
				}

				this.setFullfillmentsProductIds(this.selectedOrder);
				this.autoFillFromOrder(this.selectedOrder);
			} catch (e) {
				console.error(e);
			} finally {
				await this.base.dismissLoading();
			}
		}
	}

	/**
	 * Upon loading the modal, get the list of recipes (VOH/DV) or the list of products (MP) based on
	 * the base product id. If there are multiple bases (combined milk), prioritize EBM--but this can
	 * be passed through the parent page via this.productId?.
	 */
	async getRecipes(
		pageType: RecipePageType,
		baseProductId: Product['id'],
	): Promise<Recipe[]> {
		if (
			pageType === RecipePageType.EBM ||
			pageType === RecipePageType.DM ||
			pageType === RecipePageType.Milk_Room_Prep
		) {
			return this.inventoryService.getRecipes({
				BaseProductId: baseProductId,
			});
		}

		if (pageType === RecipePageType.Formula) {
			return [
				...(await this.inventoryService.getRecipes({
					BaseType: BaseType.Water,
				})),
				...(await this.inventoryService.getRecipes({
					BaseType: BaseType.RTF,
				})),
			];
		}
	}

	async back() {
		if (this.hasChanges) {
			await this.showUnsavedChangesAlert();
		} else {
			await this.modalCtrl.dismiss({ selectedOrder: this.selectedOrder });
		}
	}

	async changeOrder() {
		await this.back();
	}

	/**
	 * RecipeBaseOptions are determined by both the mode and the page type.
	 * In Manual Prep, RecipeBases are derived from products, not recipes. And
	 * if the user is coming from the Formula Prep workflow, the list of products
	 * has to further be filtered to only include Water and RTF.
	 */
	getRecipeBaseOptions(
		mode: RecipeMode,
		data: {
			recipes?: Recipe[];
			products?: Product[];
		},
	): RecipeBase[] {
		let result: RecipeBase[];
		if (mode === RecipeMode.MP) {
			if (this.pageType === RecipePageType.Formula) {
				data.products = data.products.filter(
					(p) => p.type === ProductType.Water || p.type === ProductType.RTF,
				);
			}
			result = data.products.map(
				(p) =>
					({
						baseName: p.name,
						baseProductId: p.id,
						baseType: p.type,
					}) as RecipeBase,
			);
		} else if (mode === RecipeMode.VOH || mode === RecipeMode.DV) {
			if (this.pageType === RecipePageType.Formula) {
				data.recipes = data.recipes.filter(
					(r) => r.baseType === BaseType.Water || r.baseType === BaseType.RTF,
				);
			}
			result = data.recipes.map((r) => ({
				baseName: r.baseName,
				baseProductId: r.baseProductId,
				baseType: r.baseType,
			}));
		} else {
			assertExhaustive(mode);
		}

		// remove dupes
		return result.filter(
			(base, index, arr) =>
				arr
					.map((mappedBase) => mappedBase.baseProductId)
					.indexOf(base.baseProductId) === index,
		);
	}

	resetForm() {
		this.form.reset();

		// Clear out component state values
		this.finalVolume = null;
		this.baseNeeded = null;
		this.additiveCandidates = null;

		// Because the form resets when users switch between recipes modes,
		// we need to manually set the volume in case they entered it from
		// a previous screen (Print Fresh)
		this.form.patchValue({
			volume: this.volume,
		});
	}

	private async ensureRecipesLoaded(): Promise<void> {
		if (this.recipes) return;

		await this.base.presentLoading('Loading Recipes...');
		try {
			this.recipes = await this.getRecipes(
				this.pageType,
				this.resolvedBaseProductId,
			);
		} finally {
			await this.base.dismissLoading();
		}
	}

	/**
	 * When selecting the recipe mode (VOH, DV)
	 * 1. Set the [filtered] recipe list
	 * 2. Set the base options (only allow selecting on Formula)
	 * 3. Set the available calorie density options based on the [filtered] recipe list
	 *
	 * When selecting the recipe mode for Manual Prep:
	 * 1. Filtering the product list is not necessary (?)
	 * 2. Set the base options (get only Water and RTF if from Formula Prep)
	 * 3. Set the available calorie density options based on all products
	 *
	 * Formula Prep is primarily used to create a feeding type with water or RTF
	 * formula as its base substrate (rather than EBM or PDHM milk).
	 */
	// async handleModeSelected(mode: RecipeMode) {
	// 	this.mode = mode;
	// 	this.resetForm();

	// 	if (mode === RecipeMode.MP) {
	// 		this.baseOptions = this.getRecipeBaseOptions(mode, {
	// 			products: StorageService.configs.getAllProducts(),
	// 		});
	// 		this.calorieDensityOptions = getNumberRange(
	// 			StorageService.configs.tenant.productCalorieMin,
	// 			StorageService.configs.tenant.productCalorieMax,
	// 		);
	// 		return;
	// 	}

	// 	// DV/VOH: only load recipes if needed right now
	// 	const mustLoadNow =
	// 		this.pageType === RecipePageType.Formula || !!this.recipeData;
	// 	if (mustLoadNow) {
	// 		await this.ensureRecipesLoaded();

	// 		this.baseOptions = this.getRecipeBaseOptions(mode, {
	// 			recipes: this.recipes,
	// 		});
	// 		this.calorieDensityOptions = this.recipeData
	// 			? getCalorieRangeFromRecipe(this.recipes, this.recipeData.base)
	// 			: [];
	// 	} else {
	// 		// non-Formula: base is effectively fixed; no need to fetch recipes on page entry
	// 		const recipeBase = getRecipeBaseByProduct(
	// 			StorageService.configs.getProduct(this.baseProductId),
	// 		);
	// 		this.baseOptions = [recipeBase];

	// 		// show tenant range until recipes are loaded later
	// 		this.calorieDensityOptions = getNumberRange(
	// 			StorageService.configs.tenant.productCalorieMin,
	// 			StorageService.configs.tenant.productCalorieMax,
	// 		);

	// 		this.form.patchValue(
	// 			{ recipeBase, volume: this.volume },
	// 			{ emitEvent: false },
	// 		);
	// 	}
	// }

	async handleModeSelected(mode: RecipeMode) {
		this.mode = mode;

		this.syncCalorieDensityEnabledState();
		if (mode === RecipeMode.MP) {
			this.baseOptions = this.getRecipeBaseOptions(mode, {
				products: StorageService.configs.getAllProducts(),
			});
			// No calorieDensityOptions cache for MP: the range depends on a base that may not be
			// resolved yet, so the dropdown and autofill compute it live (AEP-1533).
		} else if (mode === RecipeMode.VOH || mode === RecipeMode.DV) {
			await this.ensureRecipesLoaded();
			this.baseOptions = this.getRecipeBaseOptions(mode, {
				recipes: this.recipes,
			});
			const isRecipeEdit = this.recipes && this.recipeData;
			this.calorieDensityOptions = isRecipeEdit
				? getCalorieRangeFromRecipe(this.recipes, this.recipeData.base)
				: []; // Updated to recipe values on base selection
		} else {
			assertExhaustive(mode);
		}

		/*
	        Set default Recipe Base derived from scanned milk.
	        Note: Prep Formula and Print Fresh don't have a derived base
	     */
		if (this.pageType !== RecipePageType.Formula) {
			console.log(`set base product id = ${this.resolvedBaseProductId}`);
			const recipeBase = this.baseOptions.find(
				(baseOption) => baseOption.baseProductId === this.resolvedBaseProductId,
			);
			console.log(
				`handleModeSelected selected base: ${JSON.stringify(
					recipeBase,
					null,
					2,
				)}`,
			);
			if (this.scannedMilkBottleBases.length > 1) {
				recipeBase.baseName = getCombinedBaseName(this.scannedMilkBottleBases);
			}

			this.form.patchValue({ recipeBase });

			if (mode !== RecipeMode.MP) {
				// Also load the calorie density options
				this.calorieDensityOptions = getCalorieRangeFromRecipe(
					this.recipes,
					recipeBase,
				);
			}
		}

		if (mode === RecipeMode.MP && this.selectedOrder?.calorie) {
			this.autoFillManualPrepDensity(this.selectedOrder.calorie);
		}
	}

	/**
	 * Autofills Manual Prep's target density from the order, only when the dropdown offers that
	 * value — otherwise the user is left fixing a selection they never made (AEP-1533).
	 */
	private autoFillManualPrepDensity(orderCalorie: number): void {
		const matched = this.getManualPrepCalorieDensityOptions().find(
			(c) => c === orderCalorie,
		);

		if (matched != null) {
			this.form?.patchValue(
				{ manualPrepCalorieDensity: matched },
				{ emitEvent: false },
			);
		}
	}

	// order validation

	private triggerOrderValidation() {
		console.log(
			'Triggering order validation with products from scanned milk and selected ingredients',
		);
		const milkProducts: IContentProduct[] = this.scannedMilkBottles.flatMap(
			(mb) =>
				mb.contents
					.filter((c) => c.contentProductId != null)
					.map((c) => ({
						productId: c.contentProductId,
						productName: c.contentProductName,
					})),
		);

		const ingredientProducts: IContentProduct[] =
			this.selectedProductIngredients
				.filter((x) => x.content?.productId != null)
				.map((x) => ({
					productId: String(x.content.productId),
					productName: x.content.productName,
				}));

		const products = [...milkProducts, ...ingredientProducts];
		this.orderSelectRef?.validateScannedProducts(products);
	}

	getAdditivesArray(): FormArray {
		return this.form.get('fulfillmentAdditives') as FormArray;
	}

	private buildAdditiveGroup(
		content: IOrderFulfillmentContent | null = null,
	): FormGroup {
		return this.fb.group({
			additive: [content],
			calorieDensity: [null],
		});
	}

	get selectedProductIds(): Set<string> {
		const ids = new Set<string>();
		const base = this.form?.value?.fulfillmentBase as IOrderFulfillmentContent;
		if (base?.productId) ids.add(String(base.productId));
		this.getAdditivesArray()?.controls.forEach((ctrl) => {
			const additive = ctrl.value?.additive as IOrderFulfillmentContent;
			if (additive?.productId) ids.add(String(additive.productId));
		});
		return ids;
	}

	/**
	 * The unit every target-density dropdown works in: the selected order's, falling back to the
	 * tenant default when a prep has no order.
	 */
	get calorieUom(): CalorieUom {
		// `||`, not `??`: the unit is free-text, so an empty string must fall through to the default.
		const unit =
			this.selectedOrder?.calorieMeasuredUnit ||
			StorageService.configs?.productSettings?.defaultUnitOfMeasure;

		return unit === Units.Milliliters ? Units.Milliliters : Units.Ounce;
	}

	/**
	 * The tenant's mL-per-oz rate. Resolving the fallback chain is the backend's job, so there is
	 * deliberately no local default to mask a bad value.
	 *
	 * The throw is an invariant assertion, not the user-facing path — that is
	 * `ensureConversionRateConfigured`. Nothing that reaches this getter can render before it
	 * runs: the template body is behind `*ngIf="form"`, and `form` is only assigned by
	 * `buildForm()`, which `ionViewDidEnter` calls after the guard has already dismissed.
	 * Keep that gate if the template ever grows a density control outside it.
	 */
	get mlPerOz(): number {
		const rate = StorageService.configs?.productSettings?.mlToOzConversionRate;

		if (!rate || rate <= 0) {
			throw new Error(
				`Tenant productSettings.mlToOzConversionRate must be greater than 0, got ${rate}.`,
			);
		}

		return rate;
	}

	/**
	 * Guards the page on a usable conversion rate: every density scales by it, so there is nothing
	 * safe to show without one.
	 */
	private async ensureConversionRateConfigured(): Promise<boolean> {
		const rate = StorageService.configs?.productSettings?.mlToOzConversionRate;

		if (rate && rate > 0) {
			return true;
		}

		// Present first and dismiss from the button: `presentAlert` resolves once the alert is on
		// screen, so dismissing first would race it against the modal's dismiss transition.
		await this.base.alerts.presentAlert({
			header: 'Unit Not Configured',
			message:
				'This unit has no mL-to-oz conversion rate set, so target calorie densities cannot be calculated. Please contact your administrator.',
			backdropDismiss: false,
			buttons: [
				{
					text: 'OK',
					handler: () => {
						void this.modalCtrl.dismiss();
					},
				},
			],
		});

		return false;
	}

	/**
	 * A product's caloric density in kcal/mL — the calculator's own unit — or null when it cannot
	 * bound a target (a gram-measured powder), matching the backend's `_ => 0m` base-product case.
	 */
	private getProductKcalPerMl(product: Product | null | undefined): number | null {
		if (!product?.calorieDensity) {
			return null;
		}

		switch (product.measuredUnit) {
			case Units.Milliliters:
				return product.calorieDensity;
			case Units.Ounce:
				return product.calorieDensity / this.mlPerOz;
			default:
				return null;
		}
	}

	/** A product's caloric density in the working unit; null means it sets no bound. */
	private getProductDensityInUom(
		product: Product | null | undefined,
		uom: CalorieUom,
	): number | null {
		const kcalPerMl = this.getProductKcalPerMl(product);

		return kcalPerMl == null ? null : toWorkingUom(kcalPerMl, uom, this.mlPerOz);
	}

	/**
	 * The selected base product's caloric density, in kcal/oz. Fortifying to it or below yields
	 * zero or negative fortifier volume, so it bounds the target-density options (AEP-1533).
	 */
	private getBaseCalorieDensity(): number {
		const baseProductId =
			this.form?.value?.fulfillmentBase?.productId ??
			this.resolvedBaseProductId;

		if (baseProductId == null) {
			return 0;
		}

		// 0 matches the backend's `_ => 0m`: the floor then comes from the unit's own minimum.
		return (
			this.getProductDensityInUom(
				StorageService.configs?.getProduct(String(baseProductId)),
				this.calorieUom,
			) ?? 0
		);
	}

	/** The row's selected additive product, or null if the row has no selection. */
	private getAdditiveProduct(index: number): Product | null {
		const group = this.getAdditivesArray().at(index) as FormGroup | null;
		const additive = group?.value?.additive as
			| IOrderFulfillmentContent
			| undefined;

		if (additive?.productId == null) {
			return null;
		}

		return (
			StorageService.configs?.getProduct(String(additive.productId)) ?? null
		);
	}

	/**
	 * The row's additive density in the working unit, or null if none is selected. A target must
	 * stay strictly below it.
	 */
	private getAdditiveProductCalorieDensity(index: number): number | null {
		return this.getProductDensityInUom(
			this.getAdditiveProduct(index),
			this.calorieUom,
		);
	}

	/**
	 * The unit's max target density for the row's additive, lowered by the most restrictive of its
	 * formula categories (AEP-1832).
	 */
	private getCalorieMaxForAdditive(index: number, globalMax: number): number {
		const product = this.getAdditiveProduct(index);

		if (!product) {
			return globalMax;
		}

		// globalMax fallback: an unreadable category map must not NaN the ceiling and empty the list.
		return StorageService.productCalorieMaxFor(
			product.formulaCategories,
			globalMax,
		);
	}

	/**
	 * A selectable range's bounds as **step counts**. Bounds are configured in kcal/oz, so they
	 * convert first and snap **inward** — the dropdown must never offer an out-of-range density.
	 */
	private getStepBounds(
		minInOz: number,
		maxInOz: number,
		uom: CalorieUom,
	): { minSteps: number; maxSteps: number } {
		const step = CALORIE_DENSITY_STEP[uom];

		return {
			minSteps: stepCeil(fromOz(minInOz, uom, this.mlPerOz), step),
			maxSteps: stepFloor(fromOz(maxInOz, uom, this.mlPerOz), step),
		};
	}

	/** Step bounds for an additive row, narrowed by that additive's formula categories. */
	private getStepBoundsForAdditive(
		index: number,
		uom: CalorieUom,
	): { minSteps: number; maxSteps: number } {
		const tenant = StorageService.configs?.tenant;

		return this.getStepBounds(
			tenant?.productCalorieMin ?? 0,
			this.getCalorieMaxForAdditive(index, tenant?.productCalorieMax ?? 100),
			uom,
		);
	}

	/**
	 * Target density options for an additive row: above the base and the previous row, below the
	 * row's own additive, within the unit's min/max as narrowed by its formula categories
	 * (AEP-1832). Not capped by the order's final target density (AEP-1533).
	 */
	getCalorieDensityOptionsForAdditive(index: number): number[] {
		const uom = this.calorieUom;
		const bounds = this.getStepBoundsForAdditive(index, uom);
		const baseFloor = this.getBaseCalorieDensityFloor(bounds.minSteps, uom);
		const rowCeiling = this.getCalorieDensityCeiling(
			bounds.maxSteps,
			this.getAdditiveProductCalorieDensity(index),
			uom,
		);

		if (this.mode === RecipeMode.MP || index === 0) {
			return getDensityRange(baseFloor, rowCeiling, uom);
		}

		const additivesArray = this.getAdditivesArray();
		// getRawValue(): `value` would omit the previous row while its control is disabled.
		const prevCalorie = (additivesArray.at(index - 1) as FormGroup).getRawValue()
			?.calorieDensity as number | null;
		const effectiveMin =
			prevCalorie != null
				? Math.max(baseFloor, stepRound(prevCalorie, CALORIE_DENSITY_STEP[uom]) + 1)
				: baseFloor;

		// A previous row filled to the max leaves no room for this row; getDensityRange returns [].
		return getDensityRange(effectiveMin, rowCeiling, uom);
	}

	/**
	 * Ceiling as a **step count**: the resolved max, or the highest step strictly below the
	 * product's density (the backend rejects a target at or above it). Null density = no bound.
	 */
	private getCalorieDensityCeiling(
		maxSteps: number,
		productCalorieDensity: number | null,
		uom: CalorieUom,
	): number {
		if (productCalorieDensity == null) {
			return maxSteps;
		}

		return Math.min(
			maxSteps,
			stepCeil(productCalorieDensity, CALORIE_DENSITY_STEP[uom]) - 1,
		);
	}

	/**
	 * Floor as a **step count**: the unit min, or the first step strictly above the base — the
	 * backend rejects a fortifier target at or below the base's density.
	 */
	private getBaseCalorieDensityFloor(minSteps: number, uom: CalorieUom): number {
		return Math.max(
			minSteps,
			stepFloor(this.getBaseCalorieDensity(), CALORIE_DENSITY_STEP[uom]) + 1,
		);
	}

	/**
	 * Manual Prep's floor. Its density is the recipe's *final* density, not a fortification
	 * target, so a base-only or non-caloric-additive prep correctly sits at the base's own
	 * density and must keep it selectable. With a caloric additive selected the value is
	 * submitted as that additive's target (see onSubmit), so the strict floor applies (AEP-1533).
	 */
	private getManualPrepCalorieDensityFloor(
		minSteps: number,
		uom: CalorieUom,
	): number {
		if (this.hasCaloricAdditiveSelected()) {
			return this.getBaseCalorieDensityFloor(minSteps, uom);
		}

		return Math.max(
			minSteps,
			stepRound(this.getBaseCalorieDensity(), CALORIE_DENSITY_STEP[uom]),
		);
	}

	/**
	 * True when an additive row holds a caloric additive. Non-caloric ones never reach these
	 * rows — they live in `nonCaloricAdditiveItems`.
	 */
	private hasCaloricAdditiveSelected(): boolean {
		return this.getAdditivesArray().controls.some(
			(ctrl) => (ctrl as FormGroup).getRawValue()?.additive != null,
		);
	}

	/**
	 * Manual Prep's single target-density dropdown (AEP-1608) — unit min/max and the base only,
	 * never an additive's density (AEP-1287, AEP-1533). Every selected additive's formula
	 * categories can lower the ceiling (AEP-1832).
	 */
	getManualPrepCalorieDensityOptions(): number[] {
		const uom = this.calorieUom;
		const tenant = StorageService.configs?.tenant;
		const bounds = this.getStepBounds(
			tenant?.productCalorieMin ?? 0,
			this.getManualPrepCalorieMax(tenant?.productCalorieMax ?? 100),
			uom,
		);

		return getDensityRange(
			this.getManualPrepCalorieDensityFloor(bounds.minSteps, uom),
			bounds.maxSteps,
			uom,
		);
	}

	/** Formats a density for display in the page's working unit, e.g. `"0.7 kcal/mL"`. */
	formatDensity(value: number | null | undefined): string {
		return value == null ? '--' : formatCaloricDensity(value, this.calorieUom);
	}

	/**
	 * The most restrictive formula-category ceiling among Manual Prep's additives — they share one
	 * target density. The unit's max when nothing is selected.
	 */
	private getManualPrepCalorieMax(globalMax: number): number {
		const ceilings = this.getAdditivesArray().controls.map((_, index) =>
			this.getCalorieMaxForAdditive(index, globalMax),
		);

		return ceilings.length ? Math.min(...ceilings) : globalMax;
	}

	/**
	 * Null when the row's selection is valid, otherwise the message the template shows while it
	 * flags the field and blocks submit (AEP-1533).
	 */
	getAdditiveDensityErrorMessage(index: number): string | null {
		const group = this.getAdditivesArray().at(index) as FormGroup | null;
		// getRawValue(): `value` would omit a disabled density control, skipping validation.
		const rawValue = group?.getRawValue();
		const additive = rawValue?.additive as IOrderFulfillmentContent | undefined;

		if (!additive) {
			return null;
		}

		const options = this.getCalorieDensityOptionsForAdditive(index);

		if (!options.length) {
			return 'No valid target density for this additive.';
		}

		const value = rawValue?.calorieDensity as number | null;
		if (value != null && !options.includes(value)) {
			return 'This target density is no longer valid.';
		}

		return null;
	}

	/**
	 * Null when Manual Prep's target density is valid; otherwise a user-facing message
	 * explaining why (AEP-1533).
	 */
	get manualPrepDensityErrorMessage(): string | null {
		if (!this.form?.value?.fulfillmentBase) {
			return null;
		}

		const options = this.getManualPrepCalorieDensityOptions();

		if (!options.length) {
			return 'No valid target density for the current base.';
		}

		const value = this.form?.value?.manualPrepCalorieDensity as number | null;
		if (value != null && !options.includes(value)) {
			return 'This target density is no longer valid.';
		}

		return null;
	}

	compareByProductId(
		a: IOrderFulfillmentContent,
		b: IOrderFulfillmentContent,
	): boolean {
		return String(a?.productId) === String(b?.productId);
	}

	isProductSelectedElsewhere(
		productId: string | number,
		currentProductId?: string | number,
	): boolean {
		const pid = String(productId);
		if (currentProductId != null && String(currentProductId) === pid)
			return false;
		return this.selectedProductIds.has(pid);
	}

	private buildIngredientOptions(): void {
		const productIdSet = new Set<string>();

		for (const ingredient of this.selectedProductIngredients) {
			const id = ingredient?.content?.productId;
			if (id != null) productIdSet.add(String(id));
		}

		for (const mb of this.scannedMilkBottles) {
			for (const c of mb.contents ?? []) {
				if (c.contentProductId != null) {
					productIdSet.add(String(c.contentProductId));
				}
			}
		}

		this.fulfillmentBaseOptions = [];
		this.fulfillmentAdditiveOptions = [];
		this.nonCaloricAdditiveItems = [];
		this.urcExemptAdditiveItems = [];

		const logicalBaseProductIds = this.logicalBaseProductIds;

		for (const id of productIdSet) {
			const product = StorageService.configs?.getProduct(id);
			if (!product) continue;

			const isNonCaloric = product.type === ProductType.NonCaloricAdditive;
			const isRestrictedFromUrc = !!product.restrictFromUrc;

			if (isNonCaloric) {
				this.nonCaloricAdditiveItems.push({
					productId: product.id,
					productName: product.name,
					type: product.type,
					isBase: product.isBase,
				});
				continue;
			}

			if (isRestrictedFromUrc) {
				this.urcExemptAdditiveItems.push({
					productId: product.id,
					productName: product.name,
					type: product.type,
					isBase: product.isBase,
				});
				continue;
			}

			const content: IOrderFulfillmentContent = {
				productId: product.id,
				productName: product.name,
				displayName: product.displayName,
				type: product.type,
				isBase: product.isBase,
			};

			if (product.isBase) {
				this.fulfillmentBaseOptions.push(content);
			}

			/*
			 * Products can be configured as both base and additive.
			 *
			 * Only exclude the actual resolved base from additives.
			 * For same-calorie donor milk, exclude every equivalent donor
			 * product ID that represents the same logical base.
			 */
			if (
				product.isAdditive &&
				!logicalBaseProductIds.has(String(product.id))
			) {
				this.fulfillmentAdditiveOptions.push(content);
			}
		}

		// EBM/donor bases with the same caloric density collapse into one legacy-style combined base option.
		const combinableBaseIds = this.getEquivalentHumanMilkBaseIds(
			this.fulfillmentBaseOptions,
		);

		if (combinableBaseIds.size >= 2) {
			// Equivalent bases form one logical base, so they can't also appear as additive options.
			this.fulfillmentAdditiveOptions = this.fulfillmentAdditiveOptions.filter(
				(option) => !combinableBaseIds.has(String(option.productId)),
			);

			this.fulfillmentBaseOptions = this.collapseEquivalentBaseOptions(
				this.fulfillmentBaseOptions,
				combinableBaseIds,
			);

			const combinedOption = this.fulfillmentBaseOptions.find((option) =>
				combinableBaseIds.has(String(option.productId)),
			);
			this.combinedBaseProductId = combinedOption
				? String(combinedOption.productId)
				: null;
			this.combinedBaseLabel = combinedOption?.displayName ?? null;
		} else {
			this.combinedBaseProductId = null;
			this.combinedBaseLabel = null;
		}

		if (this.fulfillmentBaseOptions.length === 0) {
			throw new Error(
				'RecipeCalculator: opened with caloric products that have no base options. Validate product selection before opening.',
			);
		}

		// Pick the base first. We may stop early below (when the only extras have no calories or
		// are kept out of the calculator), and the Base box should still show the base.
		const selectedBase =
			this.fulfillmentBaseOptions.find(
				(option) => String(option.productId) === this.resolvedBaseProductId,
			) ?? null;

		this.form.patchValue(
			{
				fulfillmentBase: selectedBase,
			},
			{ emitEvent: false },
		);

		if (
			this.fulfillmentAdditiveOptions.length === 0 &&
			(this.nonCaloricAdditiveItems.length > 0 ||
				this.urcExemptAdditiveItems.length > 0)
		) {
			return;
		}

		if (this.fulfillmentAdditiveOptions.length === 0) {
			throw new Error(
				'RecipeCalculator: opened with caloric products that have no additive options. Validate product selection before opening.',
			);
		}

		const additivesArray = this.getAdditivesArray();
		while (additivesArray.length > 0) additivesArray.removeAt(0);
		/*
		 * Additive rows should be based only on the number of unique
		 * additive products. Multiple physical base products may represent
		 * one logical base, such as same-calorie donor milk from different
		 * manufacturers.
		 */
		const rowCount = new Set(
			this.fulfillmentAdditiveOptions.map((product) =>
				String(product.productId),
			),
		).size;
		for (let i = 0; i < rowCount; i++) {
			additivesArray.push(this.buildAdditiveGroup());
		}

		if (this.mode !== RecipeMode.MP) {
			additivesArray.controls.forEach((ctrl, i) => {
				if (i > 0) {
					(ctrl as FormGroup)
						.get('calorieDensity')
						.disable({ emitEvent: false });
				}
			});
		}

		additivesArray.valueChanges.subscribe(() =>
			this.syncCalorieDensityEnabledState(),
		);
	}

	// The parent page already computes this (same-density check + donor multi-manufacturer rule); just scope it to the current base options.
	private getEquivalentHumanMilkBaseIds(
		baseOptions: IOrderFulfillmentContent[],
	): Set<string> {
		const optionIds = new Set(
			baseOptions.map((option) => String(option.productId)),
		);
		const combinableIds = this.equivalentBaseProductIds
			.map(String)
			.filter((id) => optionIds.has(id));

		return combinableIds.length >= 2 ? new Set(combinableIds) : new Set();
	}

	// Collapses equivalent human-milk bases into one dropdown entry, keeping the representative's real productId/productName and only overriding displayName.
	private collapseEquivalentBaseOptions(
		baseOptions: IOrderFulfillmentContent[],
		combinableBaseIds: Set<string>,
	): IOrderFulfillmentContent[] {
		const equivalentOptions = baseOptions.filter((option) =>
			combinableBaseIds.has(String(option.productId)),
		);

		if (equivalentOptions.length < 2) {
			return baseOptions;
		}

		const representative =
			equivalentOptions.find(
				(option) => String(option.productId) === this.resolvedBaseProductId,
			) ?? equivalentOptions[0];

		// Each equivalent base can be scanned either as a milk bottle (EBM) or as a
		// standalone product ingredient (donor). Use whichever scanned label is
		// available so the combined name matches legacy's "20 kcal/oz DM-Term + 20 kcal/oz EBM" format.
		const combinedName = equivalentOptions
			.map((option) =>
				this.getScannedBaseLabel(
					String(option.productId),
					option.displayName || option.productName,
				),
			)
			.join(' + ');

		const combinedOption: IOrderFulfillmentContent = {
			...representative,
			displayName: combinedName,
		};

		const nonEquivalentOptions = baseOptions.filter(
			(option) => !combinableBaseIds.has(String(option.productId)),
		);

		return [combinedOption, ...nonEquivalentOptions];
	}

	private getScannedBaseLabel(productId: string, fallbackName: string): string {
		const scannedBottleBase = this.scannedMilkBottleBases.find(
			(base) => String(base.baseProductId) === productId,
		);
		if (scannedBottleBase) {
			return scannedBottleBase.baseName;
		}

		const scannedIngredient = this.selectedProductIngredients.find(
			(ingredient) => String(ingredient.content?.productId) === productId,
		);

		return scannedIngredient?.matchedScans?.[0]?.name ?? fallbackName;
	}

	/**
	 * Unlocks each row's Target Density sequentially:
	 * - Row 0 unlocks when its additive is selected.
	 * - Row N unlocks when row N-1's calorieDensity has a value.
	 */
	syncCalorieDensityEnabledState(): void {
		const array = this.getAdditivesArray();
		array.controls.forEach((ctrl, i) => {
			const group = ctrl as FormGroup;
			const calCtrl = group.get('calorieDensity');
			const shouldEnable =
				this.mode === RecipeMode.MP ||
				i === 0 ||
				!!array.at(i - 1).value?.calorieDensity;

			if (shouldEnable && calCtrl.disabled) {
				calCtrl.enable({ emitEvent: false });
			} else if (!shouldEnable && calCtrl.enabled) {
				calCtrl.disable({ emitEvent: false });
			}
		});
	}

	/**
	 * Pre-populates `numberOfFeeds` from the selected order's interval and the
	 * tenant's prep timeframe.  Only applies in DV mode and only when the user
	 * hasn't manually overridden the volume field.
	 */
	private autoFillFromOrder(order: PatientEhrOrderModel) {
		// --- Volume fields ---
		const amount = order?.amount ?? null;
		const overfill =
			order?.overfillAmountInMl ??
			StorageService.configs?.productSettings?.overFillVolume ??
			0;

		if (amount != null) {
			this.form.patchValue({ volumePerFeed: amount }, { emitEvent: false });
		}
		this.form.patchValue({ overfillPerFeed: overfill }, { emitEvent: false });

		if (order?.interval) {
			const intervalHours = parseIntervalToHours(order.interval);
			const prepTimeframeHours = getPrepTimeframeHours(order.base);

			if (prepTimeframeHours != null && intervalHours != null) {
				const feeds = calculateNumberOfFeeds(prepTimeframeHours, intervalHours);

				this.form.patchValue({ numberOfFeeds: feeds }, { emitEvent: false });
			} else {
				this.form.patchValue({ numberOfFeeds: null }, { emitEvent: false });
			}
		} else {
			this.form.patchValue({ numberOfFeeds: null }, { emitEvent: false });
		}

		// --- Ingredients: Calorie Density ---
		// Snapping to the closest value is fine here: these options are recipe-derived and only
		// drive additiveTypeOptions — this control is never submitted as a fortifier target.
		// Skipped in kcal/mL mode: rows store kcal/oz, so snapping would pick a nonsense value.
		if (
			this.mode !== RecipeMode.MP &&
			this.calorieUom === Units.Ounce &&
			order?.calorie &&
			this.calorieDensityOptions?.length
		) {
			const matched =
				this.calorieDensityOptions.find((c) => c === order.calorie) ??
				this.closestCalorieDensity(order.calorie);
			if (matched != null) {
				this.form.patchValue({ calorieDensity: matched }, { emitEvent: false });
				if (this.recipes) {
					this.additiveTypeOptions = getAdditivesByCalorieDensity(
						this.recipes,
						matched,
					);
				}
			}
		}

		this.handleFeedInputsChanged();
		this.buildIngredientOptions();

		// --- MP mode: autofill single target density from order ---
		if (this.mode === RecipeMode.MP && order?.calorie) {
			this.autoFillManualPrepDensity(order.calorie);
		}

		// --- Last additive: auto-fill calorie density from order target ---
		// Only within the row's own range, which getCalorieDensityOptionsForAdditive already knows.
		if (this.mode !== RecipeMode.MP) {
			const additivesArray = this.getAdditivesArray();
			const lastIndex = additivesArray.length - 1;

			if (lastIndex >= 0) {
				const target = this.getAutofillableTargetDensity(
					order?.calorie,
					lastIndex,
				);

				if (target != null) {
					(additivesArray.at(lastIndex) as FormGroup)
						.get('calorieDensity')
						.patchValue(target, { emitEvent: false });
				}
			}
		}
	}

	/**
	 * `orderCalorie` when the row's range allows it, otherwise null — better to leave the row for
	 * the user than to autofill past the bounds (AEP-1533).
	 */
	private getAutofillableTargetDensity(
		orderCalorie: number | null | undefined,
		rowIndex: number,
	): number | null {
		if (orderCalorie == null) return null;

		const options = this.getCalorieDensityOptionsForAdditive(rowIndex);
		return options.includes(orderCalorie) ? orderCalorie : null;
	}

	private closestCalorieDensity(target: number): number | null {
		if (!this.calorieDensityOptions?.length) return null;
		return this.calorieDensityOptions.reduce((prev, curr) =>
			Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev,
		);
	}

	async handleSelectOrderChanged(ev) {
		const previousUom = this.calorieUom;
		this.selectedOrder = ev;
		this.clearDensitiesIfUomChanged(previousUom);
		this.setFullfillmentsProductIds(this.selectedOrder);
		this.resetAdditive();
		this.autoFillFromOrder(this.selectedOrder);
		if (this.isRestrictedOnlyMode && this.mode !== RecipeMode.MP) {
			await this.handleModeSelected(RecipeMode.MP);
		}
		this.calculate();
		this.triggerOrderValidation();
	}

	setFullfillmentsProductIds(order: PatientEhrOrderModel): void {
		this.fullfillmentsProductIds.clear();
		order?.fulfillments?.forEach((f) =>
			f.contents?.forEach((c) =>
				this.fullfillmentsProductIds.add(String(c.productId)),
			),
		);
	}

	// isMilkBottleOrderMatch(milkBottleId: string): boolean {
	// 	return isMilkBottleOrderMatch(milkBottleId, this.validationResults);
	// }

	private get urcAdditives(): IAdditive[] {
		return this.getAdditivesArray()
			.getRawValue()
			.filter((row) => row.additive?.productId != null)
			.map((row) => ({
				productId: String(row.additive.productId),
				productName: row.additive.productName,
				productType: row.additive.type ?? '',
				productMeasuredUnit: '',
				productAmountPerMl: 0,
				productDisplacementPerUnit: 0,
				productEnforceScanning: false,
				productContainsHumanMilk: false,
			}));
	}

	/**
	 * Base product ID resolved exclusively from the scanned milk bottles and selected
	 * product ingredients via the priority helper. Returns null when neither list
	 * contains a resolvable base — the calculator should not proceed in that case.
	 */
	private get resolvedBaseProductId(): string | null {
		const id = resolveBase(
			this.scannedMilkBottles,
			this.selectedProductIngredients,
		)?.productId;
		return id != null ? String(id) : null;
	}

	private get logicalBaseProductIds(): Set<string> {
		const ids = new Set<string>();

		if (!this.resolvedBaseProductId) {
			return ids;
		}

		ids.add(this.resolvedBaseProductId);

		const equivalentIds = this.equivalentBaseProductIds.map(String);

		if (equivalentIds.includes(this.resolvedBaseProductId)) {
			for (const id of equivalentIds) {
				ids.add(id);
			}
		}

		return ids;
	}

	private get urcBase(): RecipeBase | null {
		const content = this.form.value.fulfillmentBase as IOrderFulfillmentContent;
		if (!content?.productId) return null;
		return {
			baseName: content.productName,
			baseProductId: String(content.productId),
			baseType: content.type ?? null,
		};
	}

	validateRecipeMatch() {
		if (!StorageService.isOrderValidationEnforced) {
			console.log('Skip validation because no orders');
			return;
		}

		// In kcal/mL mode form.calorieDensity is not populated (see autoFillFromOrder), so
		// finalCalorieDensity — the last additive row — is the right value.
		const calorieDensity =
			this.form.value.calorieDensity ?? this.finalCalorieDensity;
		const additives = this.scannedAdditives.length
			? this.scannedAdditives
			: this.urcAdditives;
		const base = this.selectedRecipesBases?.[0] ?? this.urcBase;

		if (!calorieDensity || !additives.length || !base) return;

		this.orderSelectRef.validateOrderMatchesRecipeCalculation({
			calorieDensity,
			additives,
			base,
		});
	}

	validateSelectedOrderMatchesManualPrep() {
		if (!StorageService.isOrderValidationEnforced) {
			console.log('Skip validation because no orders');
			return;
		}

		this.throwIfSelectedRecipesBasesIsInvalid();

		const calorieDensity = this.form.value.manualPrepCalorieDensity as number;
		const products = this.scannedProducts.map((p) => ({
			productId: p.id,
			productName: p.name,
		}));

		let bases: RecipeBase[] = [];
		if (this.scannedMilkBottleBases.length > 0) {
			bases = this.scannedMilkBottleBases;
		} else {
			bases = this.selectedRecipesBases;
		}

		this.orderSelectRef.validateSelectedOrderMatchesManualPrep({
			calorieDensity,
			products,
			recipeBases: bases,
		});
	}

	// additive stuff

	formRecipes(): FormArray {
		return this.form.get('recipes') as FormArray;
	}

	newAdditive(): FormGroup {
		return this.fb.group({
			additive: '',
		});
	}

	addAdditive() {
		this.formRecipes().push(this.newAdditive());
	}

	removeAdditiveQuantity(i: number) {
		this.formRecipes().removeAt(i);
	}

	resetAdditive() {
		for (let i = 0; i < this.formRecipes().length; i++) {
			if (i === 0) {
				this.formRecipes().at(i).patchValue({ additive: null });
			} else {
				this.removeAdditiveQuantity(i);
			}
		}

		this.scanData = null;
		this.scannedAdditives = [];
	}

	// product stuff

	formProducts(): FormArray {
		return this.form.get('products') as FormArray;
	}

	addProduct() {
		this.formProducts().push(this.newProduct());
	}

	newProduct(): FormGroup {
		return this.fb.group({
			product: '',
		});
	}

	resetProduct() {
		for (let i = 0; i < this.formProducts().length; i++) {
			if (i === 0) {
				this.formProducts().at(i).patchValue({ product: null });
			} else {
				this.removeProductQuantity(i);
			}
		}

		this.scanData = null;
		this.scannedAdditives = [];
	}

	removeProductQuantity(i: number) {
		this.formProducts().removeAt(i);
	}

	throwIfSelectedRecipesBasesIsInvalid() {
		if (!this.selectedRecipesBases || this.selectedRecipesBases.length === 0) {
			// biome-ignore lint/nursery/noSecrets: <explanation>
			throw new Error('selectedRecipesBases is null or empty');
		}
		// we're suppose to only have one baseProductId at this point in the workflow
		if (this.selectedRecipesBases.length > 1) {
			throw new Error(
				// biome-ignore lint/nursery/noSecrets: <explanation>
				"there's more than one baseProductId in selectedRecipesBases",
			);
		}
	}

	/** Converts a density in the working unit to kcal/oz, for fields whose contract is kcal/oz. */
	private toKcalPerOz(value: number): number {
		return this.calorieUom === Units.Ounce ? value : value * this.mlPerOz;
	}

	/** Builds the calculator request: densities travel in the working unit, tagged with it. */
	private buildUrcInput(
		fv: ReturnType<typeof this.form.getRawValue>,
		additivesRaw: Array<{
			additive: IOrderFulfillmentContent;
			calorieDensity: number | null;
		}>,
	): UrcUserInput {
		const base = fv.fulfillmentBase as IOrderFulfillmentContent | null;
		const mpCalorieDensity =
			this.mode === RecipeMode.MP ? (fv.manualPrepCalorieDensity ?? 0) : 0;

		return {
			caloricDensityUnit: this.calorieUom,
			products: [
				...(base
					? [
							{
								id: String(base.productId),
								name: base.productName,
								targetCaloricDensity: 0,
							},
						]
					: []),
				...additivesRaw
					.filter((a) => a.additive)
					.map((a) => ({
						id: String(a.additive.productId),
						name: a.additive.productName,
						targetCaloricDensity:
							this.mode === RecipeMode.MP
								? mpCalorieDensity
								: (a.calorieDensity ?? 0),
					})),
			],
			volumeOnHandInput: { volumeOnHandInMl: Number(fv.volumeVOH) },
			finalDesiredVolumeInput: { desiredVolumeInMl: Number(fv.volume) },
		};
	}

	async onSubmit() {
		const fv = this.form.getRawValue();

		const base = fv.fulfillmentBase as IOrderFulfillmentContent;
		const additivesRaw = this.getAdditivesArray().getRawValue() as Array<{
			additive: IOrderFulfillmentContent;
			calorieDensity: number | null;
		}>;

		const input: UrcUserInput = this.buildUrcInput(fv, additivesRaw);

		let urcResult: UrcResult;
		try {
			urcResult = await this.urcService.calculateRecipe(this.mode, input);
		} catch (error) {
			if (error instanceof RecipeNotSatisfiableApiError) {
				// No recipe can satisfy the products' UOM constraints (AEP-1531). Warn the user and
				// keep them in the calculator instead of building a recipe.
				await this.showRecipeNotSatisfiableAlert(error.details);
				return;
			}
			throw error;
		}

		const recipeData = this.buildRecipeData(
			urcResult,
			base,
			fv,
			additivesRaw,
		);
		await this.modalCtrl.dismiss({
			recipeData,
			selectedOrder: this.selectedOrder,
		});
	}

	private buildRecipeData(
		urcResult: UrcResult,
		base: IOrderFulfillmentContent,
		fv: ReturnType<typeof this.form.getRawValue>,
		additivesRaw: Array<{
			additive: IOrderFulfillmentContent;
			calorieDensity: number | null;
		}>,
	): RecipeData {
		const baseProduct = StorageService.configs?.getProduct(
			String(base?.productId),
		);

		// Carry the combined label through to the recipe page when the selected base is the collapsed one.
		const isCombinedBase =
			this.combinedBaseProductId != null &&
			String(base?.productId) === this.combinedBaseProductId;

		const recipeBase: RecipeBase | null = base
			? {
					baseName:
						isCombinedBase && this.combinedBaseLabel
							? this.combinedBaseLabel
							: base.productName,
					baseProductId: String(base.productId),
					baseType: baseProduct?.type ?? ProductType.Formula,
					measuredUnit: baseProduct.measuredUnit ?? null,
				}
			: null;

		const volume =
			this.mode === RecipeMode.VOH ? Number(fv.volumeVOH) : Number(fv.volume);

		const baseResult = urcResult.products[0];
		const additiveResults = urcResult.products.slice(1);

		const additiveCandidates: AdditiveCandidate[] = additiveResults.map((p) => {
			const product = StorageService.configs?.getProduct(p.productId) ?? null;
			const iAdditive: IAdditive | null = product
				? {
						productId: String(product.id),
						productName: product.name,
						productType: product.type,
						productMeasuredUnit: String(product.measuredUnit),
						productAmountPerMl: 0,
						productDisplacementPerUnit: product.displacementPerUnit ?? 0,
						productEnforceScanning: product.enforceScanning ?? false,
						productContainsHumanMilk: product.containsHumanMilk ?? false,
					}
				: null;
			return {
				additive: iAdditive,
				product,
				additiveAmount: p.amountNeeded,
				baseAmount: baseResult?.amountNeeded ?? 0,
				fortifierVolumeInMl: p.fortifierVolumeInMl,
				caloricDensity: p.caloricDensity,
			};
		});

		const nonCaloricCandidates: AdditiveCandidate[] =
			this.nonCaloricAdditiveItems.map((item) => {
				const product =
					StorageService.configs?.getProduct(String(item.productId)) ?? null;
				const iAdditive: IAdditive | null = product
					? {
							productId: String(product.id),
							productName: product.name,
							productType: product.type,
							productMeasuredUnit: String(product.measuredUnit),
							productAmountPerMl: 0,
							productDisplacementPerUnit: product.displacementPerUnit ?? 0,
							productEnforceScanning: product.enforceScanning ?? false,
							productContainsHumanMilk: product.containsHumanMilk ?? false,
						}
					: null;
				return {
					additive: iAdditive,
					product,
					additiveAmount: 0,
					baseAmount: baseResult?.amountNeeded ?? 0,
					fortifierVolumeInMl: null,
					caloricDensity: 0,
					isNonCaloricAdditive: true,
				};
			});

		const urcExemptCandidates: AdditiveCandidate[] =
			this.urcExemptAdditiveItems.map((item) => {
				const product =
					StorageService.configs?.getProduct(String(item.productId)) ?? null;
				const iAdditive: IAdditive | null = product
					? {
							productId: String(product.id),
							productName: product.name,
							productType: product.type,
							productMeasuredUnit: String(product.measuredUnit),
							productAmountPerMl: 0,
							productDisplacementPerUnit: product.displacementPerUnit ?? 0,
							productEnforceScanning: product.enforceScanning ?? false,
							productContainsHumanMilk: product.containsHumanMilk ?? false,
						}
					: null;
				return {
					additive: iAdditive,
					product,
					additiveAmount: 0,
					baseAmount: baseResult?.amountNeeded ?? 0,
					fortifierVolumeInMl: null,
					caloricDensity: null,
					isUrcExempt: true,
				};
			});

		const isManualPrepDensity =
			this.mode === RecipeMode.MP && fv.manualPrepCalorieDensity != null;

		// calorieDensity stays kcal/oz for existing consumers; MP's value converts from
		// the working unit.
		const calorieDensityInOz = isManualPrepDensity
			? this.toKcalPerOz(fv.manualPrepCalorieDensity as number)
			: urcResult.finalCaloricDensityKcalPerOz;

		// The density in the clinician's unit. Manual Prep never calls the calculator, so there its
		// selection *is* the final density; other modes show what the calculator returned.
		const displayDensity = isManualPrepDensity
			? (fv.manualPrepCalorieDensity as number)
			: toWorkingUom(
					urcResult.finalCaloricDensityKcalPerMl,
					this.calorieUom,
					this.mlPerOz,
				);

		return {
			pageType: this.pageType,
			mode: urcResult.mode,
			base: recipeBase,
			calorieDensity: calorieDensityInOz,
			caloricDensityDisplay: this.formatDensity(displayDensity),
			volume,
			finalVolume: urcResult.finalVolume,
			baseAmount: baseResult?.amountNeeded ?? 0,
			scanData: this.scanData,
			additiveCandidates: [
				...additiveCandidates,
				...nonCaloricCandidates,
				...urcExemptCandidates,
			],
			selectedFulfillment: this.selectedOrder?.fulfillments?.[0],
		};
	}

	// on change
	/**
	 * Whenever base type changes,
	 * every other field needs to be cleared
	 * calorie options need to be recalculated
	 *
	 * None of this applies on manual prep mode
	 */
	baseOptionChanged(mode: RecipeMode, recipeBase: RecipeBase) {
		console.log(
			`RecipeCalculatorModal.baseOptionChanged: base = ${JSON.stringify(
				recipeBase,
				null,
				2,
			)}`,
		);

		if (mode === RecipeMode.MP) {
			console.warn(
				`RecipeCalculatorModal.baseOptionChanged: don't filter on calories when base is changed in manual prep mode.`,
			);
		} else if (mode === RecipeMode.VOH || mode === RecipeMode.DV) {
			this.calorieDensityOptions = getCalorieRangeFromRecipe(
				this.recipes,
				recipeBase,
			);
			this.form.patchValue({
				calorieDensity: null,
			});
			this.resetAdditive();
			this.resetProduct();
		} else {
			assertExhaustive(mode);
		}
	}

	volumeChanged() {
		console.log(this.form.value.volume);
		this.calculate();
	}

	/**
	 * ML-707 Additive should be cleared when selecting a new calorie density
	 */
	calorieDensityChanged(ev) {
		ev.preventDefault();
		const calorieDensity = ev.target.value;
		this.form.patchValue({
			calorieDensity: calorieDensity || null,
		});
		console.log(
			`RecipeCalculatorModal.calorieDensityChanged: calorie density = ${calorieDensity}`,
		);

		if (this.mode === RecipeMode.MP) {
			console.warn(
				`RecipeCalculatorModal.calorieDensityChanged: don't filter products when calorie is changed in manual prep mode.`,
			);
		} else {
			this.additiveTypeOptions = getAdditivesByCalorieDensity(
				this.recipes,
				calorieDensity,
			);
			this.resetAdditive();
		}

		this.calculate();
	}

	handleSelectAdditive(scanData: ScannedRecipeData) {
		this.resetAdditive();

		console.log(
			`handleSelectAdditive: scanned objects: ${JSON.stringify(
				scanData,
				null,
				2,
			)}`,
		);
		for (const [i, recipe] of scanData.selectedRecipes.entries()) {
			if (i > 0) {
				this.addAdditive();
			}
			this.formRecipes()
				.at(i)
				.patchValue({
					additive: displayAdditives({
						recipe: recipe.recipe,
					}),
				});
		}
		this.scanData = scanData;
		this.scannedAdditives = getScanDataAdditives(scanData);
		this.calculate();
	}

	handleSelectProduct(scanData: ScannedRecipeData) {
		this.resetProduct();

		console.log(
			`handleSelectProduct: scanned objects: ${JSON.stringify(
				scanData,
				null,
				2,
			)}`,
		);
		for (const [i, selectedProduct] of scanData.selectedProducts.entries()) {
			if (i > 0) {
				this.addProduct();
			}
			this.formProducts()
				.at(i)
				.patchValue({ product: selectedProduct.product.name });
		}
		this.scanData = scanData;
		this.scannedProducts = scanData.selectedProducts.map((s) => s.product);
		// this.scannedAdditives = getScanDataAdditives(scanData);
		this.calculate();
	}

	/**
	 * Use the volume increments of the additive to recalculate the additive amount, base amount, and total volume
	 * (adjusted volume) For Volume On Hand, if the base volume is less than the increment amount
	 *
	 * If recipe form is empty, make sure results are cleared
	 */
	calculate() {
		const calorieDensity =
			this.form.value.calorieDensity ?? this.finalCalorieDensity;
		const recipe = this.scannedAdditives;
		const volume = Number(
			this.mode === RecipeMode.VOH
				? this.form.value.volumeVOH
				: this.form.value.volume,
		);

		if (calorieDensity && (recipe.length || this.urcAdditives.length)) {
			this.validateRecipeMatch();
		}

		if (
			this.mode === RecipeMode.MP &&
			calorieDensity &&
			this.scannedProducts?.length
		) {
			this.validateSelectedOrderMatchesManualPrep();
		}

		if (calorieDensity && recipe.length && volume) {
			const additives = recipe.map((r) => {
				const additive = new Additive(r);
				additive.product = StorageService.configs.getProduct(r.productId);
				additive.increment = additive.product.additiveIncrement;
				return additive;
			});

			if (this.mode === RecipeMode.VOH) {
				// this.calculateVolumeOnHand(volume, recipe);
				this.handleVolumeOnHandChange(volume, additives);
			} else if (this.mode === RecipeMode.DV) {
				// this.calculateDesiredVolume(volume, recipe);
				this.handleDesiredVolumeChange(volume, additives);
			}
		}
	}

	handleVolumeOnHandChange(volumeOnHand: number, scannedAdditives: Additive[]) {
		const minimumPossibleVolume = getMinimumPossibleVolume(scannedAdditives);
		if (volumeOnHand < minimumPossibleVolume) {
			this.showMinimumPossibleVolumeAlert(minimumPossibleVolume);
		} else {
			this.additiveCandidates = calculateVolumeOnHand(
				volumeOnHand,
				scannedAdditives,
			);
			const baseAmount = this.additiveCandidates[0].baseAmount;

			const totalAdditiveAmount = getTotalAdditiveAmount(
				baseAmount,
				scannedAdditives,
			);

			// Always round up to the nearest 0.01 for final volume
			const finalVolume = getFinalVolume(baseAmount, scannedAdditives, 0.01);

			console.log(
				`base_amt=${baseAmount}, ttl_add_amt=${totalAdditiveAmount}, final_vol=${finalVolume}`,
			);

			this.setCalculationResults(baseAmount, finalVolume);
		}
	}
	handleDesiredVolumeChange(
		desiredVolume: number,
		scannedAdditives: Additive[],
	) {
		// const minimumPossibleVolume =
		// 	getMinimumPossibleVolume(scannedAdditives);
		// if (volumeOnHand < minimumPossibleVolume) {
		// 	this.showMinimumPossibleVolumeAlert(minimumPossibleVolume);
		// } else {
		this.additiveCandidates = calculateDesiredVolume(
			desiredVolume,
			scannedAdditives,
		);
		const baseAmount = this.additiveCandidates[0].baseAmount;

		const totalAdditiveAmount = getTotalAdditiveAmount(
			baseAmount,
			scannedAdditives,
		);

		// Always round up to the nearest 0.01 for final volume
		const finalVolume = getFinalVolume(baseAmount, scannedAdditives, 0.01);

		console.log(
			`base_amt=${baseAmount}, ttl_add_amt=${totalAdditiveAmount}, final_vol=${finalVolume}`,
		);

		this.setCalculationResults(baseAmount, finalVolume);
		// }
	}

	/**
	 * Final Volume must be rounded 2 decimal places
	 */
	setCalculationResults(baseAmount: number, finalVolume: number) {
		this.baseNeeded = baseAmount;
		console.table({ finalVolume });
		this.finalVolume = finalVolume; // Math.round(finalVolume * 100) / 100;
	}

	/**
	 * This is basically only relevant to rounding increments in grams.
	 * If Volume On Hand, round down to the nearest increment
	 * If Desired Volume, round up to the nearest increment
	 *
	 * If additive amount is in mLs, this doesn't really do anything because the search algorithm will match the amount
	 * on whole numbers anyway.
	 */
	getAdditiveAmount(mode: RecipeMode, amount: number): number {
		if (mode === RecipeMode.VOH) {
			return Math.floor(amount * 10000) / 10000;
		}

		if (mode === RecipeMode.DV) {
			return Math.ceil(amount * 10000) / 10000;
		}

		return amount;
	}

	// Booleans

	hasRecipeBase(): boolean {
		return this.form.value.recipeBase;
	}

	hasCalorieDensity(): boolean {
		return this.form.value.calorieDensity;
	}

	hasSelectableBase(): boolean {
		return this.pageType === RecipePageType.Formula;
	}

	clearFulfillmentBase(): void {
		this.form.patchValue({ fulfillmentBase: null });
	}

	clearAdditive(index: number): void {
		(this.getAdditivesArray().at(index) as FormGroup).patchValue({
			additive: null,
			calorieDensity: null,
		});
		this.syncCalorieDensityEnabledState();
	}

	clearAdditiveDensity(index: number): void {
		(this.getAdditivesArray().at(index) as FormGroup).patchValue({
			calorieDensity: null,
		});
		this.syncCalorieDensityEnabledState();
	}

	clearManualPrepDensity(): void {
		this.form.patchValue({ manualPrepCalorieDensity: null });
	}

	/**
	 * Drops every selected target density when the working unit changes — leaving `24` in place
	 * after a switch to kcal/mL would submit 24 kcal/mL.
	 */
	private clearDensitiesIfUomChanged(previousUom: CalorieUom): void {
		if (previousUom === this.calorieUom) {
			return;
		}

		this.getAdditivesArray().controls.forEach((ctrl) =>
			(ctrl as FormGroup).patchValue(
				{ calorieDensity: null },
				{ emitEvent: false },
			),
		);
		this.form?.patchValue(
			{ calorieDensity: null, manualPrepCalorieDensity: null },
			{ emitEvent: false },
		);
		this.syncCalorieDensityEnabledState();
	}

	handleManualPrepDensityChanged(): void {
		if (this.mode === RecipeMode.MP && this.scannedProducts?.length) {
			this.validateSelectedOrderMatchesManualPrep();
		}
	}

	canSubmit(mode: RecipeMode, form: FormGroup): boolean {
		const volume =
			mode === RecipeMode.VOH ? form.value.volumeVOH : form.value.volume;
		if (!volume) return false;

		if (!form.value.fulfillmentBase) return false;

		const additivesRaw = this.getAdditivesArray().getRawValue() as Array<{
			additive: IOrderFulfillmentContent | null;
			calorieDensity: number | null;
		}>;

		if (mode === RecipeMode.MP) {
			if (form.value.manualPrepCalorieDensity == null) return false;
			if (this.manualPrepDensityErrorMessage) return false;
			return additivesRaw.every((a) => a.additive != null);
		}

		if (additivesRaw.some((_, i) => this.getAdditiveDensityErrorMessage(i))) {
			return false;
		}

		// ALL additive rows must be fully filled (additive + calorie density)
		return additivesRaw.every(
			(a) => a.additive != null && a.calorieDensity != null,
		);
	}

	get isRestrictedOnlyMode(): boolean {
		if (this.urcExemptAdditiveItems.length > 0) return true;
		return (
			this.fulfillmentAdditiveOptions.length === 0 &&
			this.nonCaloricAdditiveItems.length > 0
		);
	}

	get finalCalorieDensity(): number | null {
		if (this.mode === RecipeMode.MP) {
			return this.form?.value?.manualPrepCalorieDensity ?? null;
		}
		const additivesArray = this.getAdditivesArray();
		if (!additivesArray?.length) return null;
		const lastGroup = additivesArray.at(additivesArray.length - 1) as FormGroup;
		return lastGroup.getRawValue()?.calorieDensity ?? null;
	}

	// Alerts

	/**
	 * Going back will remove all entered recipe information. Continue?
	 */
	async showUnsavedChangesAlert() {
		await this.base.alerts.presentAlert({
			header: 'Unsaved Changes',
			subHeader: '',
			message:
				'Going back will remove all entered recipe information. Continue?',
			backdropDismiss: false,
			buttons: [
				{
					text: 'No',
					role: 'cancel',
				},
				{
					text: 'Yes',
					handler: () => {
						this.modalCtrl.dismiss({ selectedOrder: this.selectedOrder });
					},
				},
			],
		});
	}

	async showInvalidBaseAmountAlert(baseVolume: number) {
		await this.base.alerts.presentAlert({
			header: 'Invalid Base Amount',
			message: `The base volume of ${baseVolume} mL cannot be less than the minimum required volume for this recipe.`,
			backdropDismiss: false,
			buttons: [
				{
					text: 'OK',
					handler: () => {
						const key = this.mode === RecipeMode.VOH ? 'volumeVOH' : 'volume';
						this.form.patchValue({ [key]: null });
					},
				},
			],
		});
	}

	async showMinimumPossibleVolumeAlert(minimumPossibleVolume: number) {
		await this.base.alerts.presentAlert({
			header: 'Invalid Base Amount',
			message: `The base volume entered is less than the minimum amount required for this recipe (${minimumPossibleVolume}mL)`,
			backdropDismiss: false,
			buttons: [
				{
					text: 'OK',
					handler: () => {
						const key = this.mode === RecipeMode.VOH ? 'volumeVOH' : 'volume';
						this.form.patchValue({ [key]: null });
					},
				},
			],
		});
	}

	/**
	 * Warns the user when the calculator cannot produce a recipe that satisfies the products' unit
	 * of measure constraints (AEP-1531). The header and message follow the backend's reason code:
	 *
	 * - `NotEnoughBase`: the volume entered is the problem, so the user is told the minimum base
	 *   required and the volume field is cleared so they cannot move forward with an invalid amount.
	 * - `MultipleConstrainedProducts`: the product selection is the problem, not the volume, so the
	 *   volume is left as entered and the user is pointed at the products instead.
	 */
	async showRecipeNotSatisfiableAlert(details: UrcNotSatisfiableError) {
		const isProductSelectionProblem = details.reason === 'MultipleConstrainedProducts';

		const header = isProductSelectionProblem ? 'Unsupported Product Combination' : 'Invalid Base Amount';

		let message: string;
		if (isProductSelectionProblem) {
			const constrained = details.constraints.map(c => c.productName).join(', ');
			message =
				'Recipes with more than one product that must be given in fixed amounts are not currently supported. ' +
				(constrained
					? `Please remove one of the following products and try again: ${constrained}.`
					: 'Please remove one of the constrained products and try again.');
		} else if (details.requiredBaseVolumeInMl != null) {
			message = `The volume entered is less than the minimum amount required for this recipe (${details.requiredBaseVolumeInMl}mL)`;
		} else {
			message =
				details.message ||
				"This recipe cannot be produced with the selected products' unit of measure constraints.";
		}

		await this.base.alerts.presentAlert({
			header,
			message,
			backdropDismiss: false,
			buttons: [
				{
					text: 'OK',
					handler: () => {
						if (isProductSelectionProblem) return;
						const key = this.mode === RecipeMode.VOH ? 'volumeVOH' : 'volume';
						this.form.patchValue({ [key]: null });
					},
				},
			],
		});
	}

	getClassForVolumeCalculatorFields() {
		if (this.mode === RecipeMode.VOH) return 'voh-input-container';
		return 'calculator-input-container';
	}

	additiveCandidateChanged(event) {
		console.log(`additiveCandidateChanged: ${JSON.stringify(event, null, 2)}`);
	}
}

/**
 * @deprecated - use AdditiveCandidate2
 */
export interface AdditiveCandidate {
	additive: IAdditive;
	product?: Product;
	additiveAmount: number;
	baseAmount: number;
	caloricDensity?: number;
}

export interface AdditiveCandidate2 {
	additive: Additive;
	additiveAmount: number;
	baseAmount: number;
}
